import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';
import { timelineData } from '@/mocks/homeData';
import { useEffect, useState } from 'react';

export default function Section1Hero({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.1, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);
  const [visibleSteps, setVisibleSteps] = useState(0);

  useEffect(() => {
    if (!titleVisible) {
      setVisibleSteps(0);
      return;
    }
    let step = 0;
    const timer = setInterval(() => {
      step += 1;
      setVisibleSteps(step);
      if (step >= timelineData.length) clearInterval(timer);
    }, 280);
    return () => clearInterval(timer);
  }, [titleVisible]);

  const openingStatement = '今天围绕三件事展开：第一，AI评课为什么会出现；第二，AI到底怎么评一堂思政课，AI评课到底准吗？第三，评完以后怎样服务管理者、专家、教师和学生。';

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-16 right-20 w-[380px] h-[380px] rounded-full bg-primary-100/15 blur-[140px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 left-10 w-[420px] h-[420px] rounded-full bg-accent-100/12 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-100/8 blur-[160px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-24 md:pt-28 pb-10">
        {/* 顶部标签 */}
        <div
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full glass-liquid border-accent-200/30 mb-5 transition-all duration-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="gold-dot" />
          <span className="text-lg font-body text-foreground-950 font-medium">教育部高校思政课教学质量评价监测中心</span>
        </div>

        {/* 开场引言 — 紧凑简洁 */}
        <div
          className={`max-w-3xl mb-8 md:mb-10 transition-all duration-1000 delay-150 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="mt-1.5 w-1 h-8 rounded-full bg-gradient-to-b from-primary-300 to-gold-300 flex-shrink-0" />
            <p className="text-lg md:text-xl text-foreground-950 font-body leading-relaxed">
              {openingStatement}
            </p>
          </div>
        </div>

        {/* 主体内容：左右分栏 */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
          {/* 左侧：主标题区域 */}
          <div className="lg:col-span-6">
            <h1
              className={`font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-foreground-950 leading-[1.15] tracking-tight mb-5 transition-all duration-1000 delay-200 ${
                titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              从天津样板
              <br />
              <span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">到全国方案</span>
            </h1>

            {/* Tags */}
            <div
              className={`flex flex-wrap gap-2 mb-6 transition-all duration-1000 delay-300 ${
                titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              {['评价赋能', '数智驱动', '质量跃升'].map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-1.5 rounded-full bg-primary-50/80 border border-primary-200/40 text-base font-body font-medium text-primary-700"
                >
                  {tag}
                </span>
              ))}
            </div>

            <p
              className={`text-lg md:text-xl text-foreground-950 font-body leading-relaxed max-w-lg transition-all duration-1000 delay-400 ${
                titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              天津市历经六年探索，从人工听评课起步，逐步积累课堂数据，最后发展出思政课AI智能评价系统"云智评"。这不是一个临时搭出来的演示系统，而是一项从评价改革、数据积累、模型训练到推广应用逐步走出来的工作。
            </p>
          </div>

          {/* 右侧：发展历程 */}
          <div className="lg:col-span-6">
            <div
              className={`transition-all duration-1000 delay-500 ${
                titleVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
            >
              <h3 className="font-body text-base font-semibold text-foreground-950 uppercase tracking-[0.15em] mb-5">
                发展历程
              </h3>

              <div className="space-y-0">
                {timelineData.map((item, i) => (
                  <div
                    key={item.year}
                    className={`relative flex items-start gap-3 transition-all duration-500 ${
                      visibleSteps > i ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-5'
                    }`}
                    style={{ transitionDelay: `${i * 80}ms` }}
                  >
                    {/* 连接线 */}
                    {i < timelineData.length - 1 && (
                      <div
                        className={`absolute left-[15px] top-7 bottom-0 w-px bg-gradient-to-b from-gold-300/50 to-transparent transition-all duration-700 ${
                          visibleSteps > i + 1 ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0'
                        }`}
                        style={{ transformOrigin: 'top', height: 'calc(100% + 4px)' }}
                      />
                    )}

                    {/* 节点 */}
                    <div className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-gold-50/80 border border-gold-200/40 flex items-center justify-center mt-0.5">
                      <div className={`w-2 h-2 rounded-full bg-gold-500 ${visibleSteps > i ? 'animate-pulse-slow' : ''}`} />
                    </div>

                    {/* 内容 */}
                    <div className="pb-5 flex-1">
                      <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="number-font text-xl text-gold-500 font-bold">{item.year}</span>
                        <span className="text-lg font-semibold text-foreground-950 font-body">{item.label}</span>
                      </div>
                      <p className="text-lg text-foreground-950 font-body leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}